import React, { useCallback, useState } from 'react';
import {
  Button,
  type IconType,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import {
  PiArrowBendUpRight,
  PiArrowBendUpRightFill,
  PiArrowUpRight,
  PiArrowUpRightFill,
  PiClock,
  PiClockFill,
  PiMapPinSimple,
  PiMapPinSimpleFill,
  PiNavigationArrow,
  PiNavigationArrowFill,
} from 'react-icons/pi';
import type { LayerItem, TrackDisplayToggles } from '@ais-ui/react-openlayers';
import {
  getTrackDisplayToggles,
  restyleTrackLayer,
  useLayers,
} from '@ais-ui/react-openlayers';
import VectorLayer from 'ol/layer/Vector';
import type Feature from 'ol/Feature';
import type OlGeometry from 'ol/geom/Geometry';
import type { Track, SogStop } from '@ais-ui/synthetic-geo';
import { cn } from '@ais-ui/utils';
import { useDateTimeFormat } from '../../conversions/useDateFormat';

/** One row in the display-toggle button strip. */
interface ToggleDef {
  key: keyof TrackDisplayToggles;
  tooltip: string;
  ActiveIcon: IconType;
  InactiveIcon: IconType;
}

const TOGGLES: ToggleDef[] = [
  {
    key: 'showPoints',
    tooltip: 'Show waypoints',
    ActiveIcon: PiMapPinSimpleFill,
    InactiveIcon: PiMapPinSimple,
  },
  {
    key: 'showArrows',
    tooltip: 'Show direction arrows',
    ActiveIcon: PiNavigationArrowFill,
    InactiveIcon: PiNavigationArrow,
  },
  {
    key: 'showTimestamps',
    tooltip: 'Show timestamps',
    ActiveIcon: PiClockFill,
    InactiveIcon: PiClock,
  },
  {
    key: 'showSogVector',
    tooltip: 'Show SOG vector',
    ActiveIcon: PiArrowUpRightFill,
    InactiveIcon: PiArrowUpRight,
  },
  {
    key: 'showPredictor',
    tooltip: 'Show predictor (turn-aware)',
    ActiveIcon: PiArrowBendUpRightFill,
    InactiveIcon: PiArrowBendUpRight,
  },
];

interface VectorTrackDisplayTogglesProps {
  layer: LayerItem;
}

export const VectorTrackDisplayToggles = React.memo(
  ({ layer }: VectorTrackDisplayTogglesProps) => {
    const { patchLayer } = useLayers();
    const { formatTimeWithTimeZone } = useDateTimeFormat();
    const [flags, setFlags] = useState<TrackDisplayToggles>(() =>
      getTrackDisplayToggles(layer.properties),
    );

    const track = layer.properties?.track as Track | undefined;
    const defaultColor =
      (layer.properties?.defaultColor as string) ?? '#1e88e5';

    const restyle = useCallback(
      (next: TrackDisplayToggles) => {
        if (!track) return;
        const colorBy = layer.properties?.colorBy as string | undefined;
        restyleTrackLayer(
          layer.layer as VectorLayer<Feature<OlGeometry>>,
          track,
          {
            colorBy: colorBy ?? undefined,
            color: defaultColor,
            label: layer.name,
            customSogStops: layer.properties?.customSogStops as
              | SogStop[]
              | undefined,
            formatTimestamp: (iso: string) =>
              formatTimeWithTimeZone(new Date(iso), true),
            ...next,
          },
        );
      },
      [layer, track, defaultColor, formatTimeWithTimeZone],
    );

    const toggle = useCallback(
      (key: keyof TrackDisplayToggles) => {
        const next = { ...flags, [key]: !flags[key] };
        setFlags(next);
        patchLayer(layer.id, { properties: { [key]: next[key] } });
        restyle(next);
      },
      [flags, patchLayer, layer.id, restyle],
    );

    return (
      <>
        {TOGGLES.map(({ key, tooltip, ActiveIcon, InactiveIcon }) => {
          const active = flags[key];
          const Icon = active ? ActiveIcon : InactiveIcon;
          return (
            <Tooltip key={key}>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-9 w-9 flex-shrink-0 p-0 m-0"
                  onClick={() => toggle(key)}
                >
                  <Icon
                    className={cn(
                      'size-5',
                      active ? 'text-primary' : 'text-muted-foreground',
                    )}
                  />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{tooltip}</TooltipContent>
            </Tooltip>
          );
        })}
      </>
    );
  },
);

VectorTrackDisplayToggles.displayName = 'VectorTrackDisplayToggles';
